import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { CircleLoader } from "react-awesome-loaders";
import { useSelector, useDispatch } from 'react-redux';
import { Accordion, Button, Checkbox, notify, Dropdown } from 'app/components';
import { CaretLeft } from '@phosphor-icons/react';
import { INDIAN_CITIES } from '../../event-create/constants/data.js'
import { indianStates } from '../../event-create/components/step-4/indianStates.js';
import useLoginModal from "../../login/useLoginModal";
import { formatDate } from '../../../utils/formatDate';
import { checkIsPerTicket } from "./utils";
import { cancelBookingSession, completeBookingSession, createBookings, createBookingSession } from '../api';
import 'react-loading-skeleton/dist/skeleton.css';
import './style';
import PriceBreakup from './priceBreakup.jsx';

const Checkout = () => {
  const [checked, setChecked] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loginModal = useLoginModal();
  
  const { activeRequests, userData, paymentInstance } = useSelector((state) => state.app);
  const { primaryEventData, selectedCategory, attendeesDetails, selectedStateData, priceBreakup } = useSelector(
    (state) => state.primaryBuyerFlow
  );
  const { get_to_know_details, image } = primaryEventData || {};
  const totalAttendees = checkIsPerTicket(get_to_know_details) ? selectedCategory?.count : 1;
  const isBookingInProgress = activeRequests?.includes('book-tickets');
  const isPriceLoading = activeRequests?.includes('get-price-breakup');
  
  const [attendeesData, setAttendeesData] = useState(
    attendeesDetails?.length
      ? attendeesDetails
      : Array(totalAttendees)
          .fill(null)
          .map(() => ({}))
  );

  function getStateByCity(cityName) {
    const city =  INDIAN_CITIES.find(c => c.label.toLowerCase() === cityName.toLowerCase());
    return city ? city.State : "City not found";
  }
  
  const city = JSON.parse(localStorage.getItem('selectedCity') || '{}').label;
  const [selectedState, setSelectedState] = useState(selectedStateData ?? getStateByCity(city));
    
  const handleStateChange = (id) => {
      const selectedStateValue = indianStates.find((state) => state.id === id);
      setSelectedState(selectedStateValue.label);
      setAttendeesData((prevData) => {
        const updatedData = [...prevData];
        updatedData[0] = {
          ...updatedData[0],
          state: selectedStateValue,
        };
        return updatedData; 
      });
      dispatch({ type: "SELECTED_STATE_DATA:SET", data: selectedState });
  }

  const getTotalCost = () => {
    return priceBreakup?.items?.[0]?.final_amount;
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    if(!primaryEventData) navigate('/', { replace: true });
  }, [userData?.email]);

    useEffect(() => () => {
      if (paymentInstance) {
        paymentInstance.close();
      }
    }, []);

  useEffect(() => {
    if (Object.keys(primaryEventData).length === 0) navigate(-3);
  }, [userData?.email]);
  

  useEffect(() => {
    if (window.fbq && primaryEventData && selectedCategory) {
      window.fbq("track", "InitiateCheckout", {
        content_name: primaryEventData?.name,
        content_ids: [primaryEventData.id],
        content_type: "event",
        num_items: selectedCategory.count
      });
    }
  }, []);
  

  const handleBack = () => {
    localStorage.setItem('current', selectedCategory?.count)
    navigate(-1, { replace: true });
  };

  const initiatePayment = async (bookingData, instance = paymentInstance) => {
    if (!instance) {
      console.error("Zoho Payments instance not initialized.");
      return;
    }

    try {
      const options = {
        amount: String(getTotalCost()),
        currency_code: "INR",
        payments_session_id: bookingData?.booking?.pg_order_id,
        reference_number: String(primaryEventData?.smp_id),
        currency_symbol: "₹",
        business: "Dankbarkeit Entertainment LLP",
        description: "",
        address: {
          name: userData?.name,
          email: userData?.email,
          phone: attendeesDetails?.[0]?.phone_number || userData?.mobile,
        },
      };

      const data = await instance.requestPaymentMethod(options);
      completeBookingSession(
        bookingData?.booking?.id,
        {
        amount: getTotalCost(),
        invoice_id: priceBreakup?.id,
        status: "SUCCESS",
        smp_sale_id: bookingData?.booking?.id,
        payment_id: data?.payment_id,
        order_id: bookingData?.booking?.pg_order_id,
        pay_signature: "",
      }, (successData) => {
        window.fbq("track", "Purchase", {
            value: successData?.total_price,
            currency: "INR",
            content_ids: [successData?.event_details?.id],
            content_name: successData?.event_name,
            content_type: "ticket",
            num_items: successData?.tickets_booked
        });
        notify('success', "Payment Successful! Booking your tickets...");
        navigate("/event/booking/success", {
          state: {
            bookingData: successData,
            primaryEventData,
            selectedCategory,
            attendeesDetails,
          },
          replace: true,
        });
        dispatch({ type: "CLEAR_DATA" });
      }, () => {
        navigate(`/event/${primaryEventData?.id}`, { replace: true });
      });
    } catch (err) {
      if (err.code !== "widget_closed") {
        console.error("Payment Error:", err);
        notify('error', "Payment Failed!");
      } else notify('error', "Failed to complete payment!");
      cancelBookingSession(bookingData?.booking?.id);
      navigate(`/event/${primaryEventData?.id}`, { replace: true });
    } finally {
      await instance.close();
      dispatch({ type: "CLEAR_DATA" });
    }
  };

  const handleProceed = () => {
    if (!userData?.email) {
      loginModal.onOpen();
    } else {
      if (selectedCategory?.totalPrice > 0) {
        const config = {
          account_id: "60032703179",
          domain: "IN",
          otherOptions: {
            api_key: "1003.7ea8cbf8cfac3e5960a8b76159a5f6df.be33a0230584d5fa3cf105f75df9b4b5",
          },
        };

        try {
          const bookings = {
            event_id: primaryEventData?.id,
            total_price: getTotalCost(),
            invoice_id: priceBreakup?.id,
            total_ticket_count: selectedCategory?.count,
            ticket_list: [
              {
                ticket_slap_id: selectedCategory?.id,
                count: selectedCategory?.count,
              },
            ],
            get_to_know_details: {
              details: attendeesDetails,
              state: selectedState,
            }
          };
          createBookingSession(bookings, (data) => {
            let instance;
            if (!paymentInstance) {
              instance = new window.ZPayments(config);
              dispatch({ type: 'PAYMENT:INSTANCE:SET', data: instance });
            }
            initiatePayment(data, instance);
          }, () => notify('error', 'Failed to initiate payment!'));
        } catch (e) {
          console.log(e);
          notify('error', 'Failed to initiate payment!');
        }
      } else {
        const bookings = {
          event_id: primaryEventData?.id,
          total_price: 0,
          total_ticket_count: selectedCategory?.count,
          ticket_list: [
            {
              ticket_slap_id: selectedCategory?.id,
              count: selectedCategory?.count,
            },
          ],
          get_to_know_details: {
            details: attendeesDetails,
            state: selectedState,
          },
        };
    
        createBookings(bookings,(data)=>{
          window.fbq("track", "Purchase", {
            value: getTotalCost(),
            currency: "INR",
            content_ids: [primaryEventData?.id],
            content_name: primaryEventData?.name,
            content_type: "ticket",
            num_items: selectedCategory?.count
          });
          navigate('/event/booking/success', {
            state: {
              bookingData: data?.booking,
              primaryEventData,
              selectedCategory,
              attendeesDetails,
            },
            replace: true,
          });
          dispatch({ type: "CLEAR_DATA" });
        }, () => {
          navigate(`/event/${primaryEventData?.id}`, { replace: true });
          dispatch({ type: "CLEAR_DATA" });
        });
      }
    }
  }

  useEffect(() => {
      dispatch({ type: "SELECTED_STATE_DATA:SET", data: selectedState });
   }, [selectedState]);

  if (activeRequests?.includes('book-tickets-completion')) {
    return (
      <div className="flex flex-col flex-1 justify-center items-center">
        <CircleLoader
          meshColor="#FF2459"
          lightColor="#E0E7FF"
          duration={2.5}
          desktopSize="90px"
        />
        <p className="p-4 heading-16 text-black text-center">Just a moment — your tickets are on their way 😬</p>
      </div>
    );
  }

  return (
    <div className="mt-[70px] h-full md:mt-[100px] px-6 w-full">
      <div className="p-[7px] sha-1 w-fit my-[18px]" onClick={handleBack}>
        <CaretLeft size={20.571} />
      </div>

      <div className="flex flex-col gap-10 h-auto">
        <div className="flex gap-5 items-start pt-3">
          <div className="flex flex-col gap-2 items-start">
            <h1 className="heading-16">{primaryEventData?.name}</h1>
            <p className="label-14 text-grey-70">{formatDate(primaryEventData?.start_date)}</p>
            <span className="px-3 py-1 bg-light rounded-2xl label-12 text-primary">
              {selectedCategory?.count} x {selectedCategory?.name}
            </span>
          </div>
          <img
            src={primaryEventData?.image}
            alt="event-image"
            className="w-[127px] h-[71px] rounded-xl object-cover"
          />
        </div>

        
        <Accordion
          initialState={true}
          content={
            <PriceBreakup
              eventId={primaryEventData?.smp_id}
              ticketId={selectedCategory?.id}
              state={selectedState}
              count={selectedCategory?.count}
              isPaid={selectedCategory?.is_paid}
            />
          }
          title="Payment details"
          className="payment-details"
        />
        <Dropdown
          label="State"
          className="w-full"
          width="100%"
          options={indianStates}
          value={selectedState}
          onChange={handleStateChange}
          hasSearch={true}
          />
        <div
          className="fixed bottom-0 left-0 w-full mt-auto pt-5 pb-6 px-6 flex flex-col gap-3 items-center border-t-[#FFF2F2] border-t border-solid bg-[#FDFDFD] 
          md:gap-16 md:justify-center"
        >
          <Checkbox
            checked={checked}
            label={
              <p className="text-14 text-grey-70">
                I acknowledge and accept the{" "}
                <a href="/terms-and-conditions" className="text-black underline">
                  Terms of service
                </a>{" "}
                and{" "}
                <a href="/privacy-policy" className="text-black underline">
                  Privacy policy.
                </a>
              </p>
            }
            onChange={() => setChecked(!checked)}
          />
          {isPriceLoading ? (
            <Skeleton containerClassName="h-[60px] w-full" className="h-full rounded-xl" />
          ) : (
            <Button 
              className="w-full" 
              disabled={!checked || isBookingInProgress}
              loading={isBookingInProgress}
              onClick={() => handleProceed()}
            >   
              {selectedCategory?.totalPrice > 0 ? "Pay Now" : "Confirm Booking"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Checkout;
